/* 模块可见性计算（与后端 _ROLE_LOCKED_MODS / MODULE_CATALOG 规则一致） */
export const ROLE_LOCKED_MODS = { pm: ["/warehouses", "/warehouses/scan", "/warehouse-audit", "/warehouse-dashboard"] };
export const isLockedMod = (base, key) => (ROLE_LOCKED_MODS[base] || []).some((k) => key === k || key.startsWith(k + "::"));

/** 功能关闭期间不展示无效动作，但保留完整目录参与继承和历史权限解析。 */
export function permissionChildrenForDisplay(subCatalog = [], parent) {
  return subCatalog.filter((item) => !item.hiddenInPermissions && (parent == null || item.parent === parent));
}

/** 不可见动作无法由管理员调整；保存其他权限时保留其原有显式授权与屏蔽。 */
export function preserveHiddenPermissionOverrides(next, previous = {}, subCatalog = [], grantKey = "modGranted") {
  const hidden = new Set(subCatalog.filter((item) => item.hiddenInPermissions).map((item) => item.key));
  const merge = (key) => [...new Set([...(next[key] || []).filter((value) => !hidden.has(value)), ...(previous[key] || []).filter((value) => hidden.has(value))])];
  return { ...next, [grantKey]: merge(grantKey), modDenied: merge("modDenied") };
}

// __all_authenticated__ 包括分包商及历史岗位；"*" 的页面默认仍排除分包商。
export const rolesIncludeBase = (roles, base, excludeSubForStar = false) => roles === "*"
  ? (!excludeSubForStar || base !== "subcontractor")
  : (roles || []).includes("__all_authenticated__") || (roles || []).includes(base);
const subFits = (sm, base) => rolesIncludeBase(sm.roles, base);

/* 基础角色默认可见模块 = 目录默认 ∪ 角色矩阵授予 − 角色矩阵屏蔽 − 锁定；父模块可见时带上其子栏目。
   【2026-10-24 层级继承】子键继承规则与后端 _module_ok 同源：
   · 父页面在目录默认里 → 子键按各自默认角色（fits）决定；
   · 父页面被矩阵**显式授予**（roleGranted）→ 其下全部子键（栏目 + 动作按钮）一并继承可见，
     不再按子键默认角色过滤 —— 即「授权页面 = 页面 + 全部按钮」，想单独收某个按钮再显式屏蔽。 */
export function baseDefaultSet(base, modCatalog = [], subCatalog = [], roleDenied = {}, roleGranted = {}) {
  const grantedKeys = roleGranted[base] || [];
  const d = new Set(modCatalog.filter((m) => (rolesIncludeBase(m.roles, base, true) || grantedKeys.includes(m.key)) && !(roleDenied[base] || []).includes(m.key) && !isLockedMod(base, m.key)).map((m) => m.key));
  // 角色矩阵允许单独屏蔽动作；若忽略子键屏蔽，界面会把服务端已关闭的按钮误显示为可用。
  subCatalog.forEach((sm) => {
    const granted = grantedKeys.includes(sm.key);
    const denied = (roleDenied[base] || []).includes(sm.key);
    if (d.has(sm.parent) && !isLockedMod(base, sm.key) && (granted || (!denied && (subFits(sm, base) || grantedKeys.includes(sm.parent))))) d.add(sm.key);
  });
  return d;
}

/* 账号编辑只保存相对岗位的变化；保留既有覆盖，避免给默认关闭的其他按钮制造隐式屏蔽。 */
export function accountPermissionOverrides(base, selected, defaults, subCatalog = [], existing = {}) {
  const allowed = new Set([...selected].filter((key) => !defaults.has(key) && !isLockedMod(base, key)));
  (existing.modAllowed || []).forEach((key) => { if (selected.has(key) && !isLockedMod(base, key)) allowed.add(key); });
  const denied = new Set([...defaults].filter((key) => !selected.has(key)));
  (existing.modDenied || []).forEach((key) => { if (!selected.has(key)) denied.add(key); });
  // 只有账号层显式授予父页面才会继承全部子动作，需要为被单独收回的子项写屏蔽。
  subCatalog.forEach((sm) => { if (allowed.has(sm.parent) && !selected.has(sm.key) && !defaults.has(sm.key)) denied.add(sm.key); });
  return { modAllowed: [...allowed], modDenied: [...denied] };
}

/** 保留旧调用入口，账号和公告权限统一使用相同的覆盖计算规则。 */
export function userPermissionOverrides(mods, defaults, subCatalog = [], base, existing = {}) {
  return accountPermissionOverrides(base, mods, defaults, subCatalog, existing);
}

/* 角色（内置或自定义）生效模块：基础默认 → 叠加自定义角色 modGranted / modDenied（用户级 modAllowed/modDenied 再在其上叠加） */
export function roleEffectiveSet(roleKey, roles = [], modCatalog = [], subCatalog = [], roleDenied = {}, roleGranted = {}) {
  const r = roles.find((x) => x.key === roleKey);
  const base = r?.baseRole || roleKey;
  const d = baseDefaultSet(base, modCatalog, subCatalog, roleDenied, roleGranted);
  if (r?.custom) {
    (r.modGranted || []).forEach((k) => { if (!isLockedMod(base, k)) { d.add(k); subCatalog.forEach((sm) => { if (sm.parent === k) d.add(sm.key); }); } });  // 显式授予 → 子键全部继承（层级继承，与后端一致）
    (r.modDenied || []).forEach((k) => { d.delete(k); subCatalog.forEach((sm) => { if (sm.parent === k) d.delete(sm.key); }); });
  }
  return d;
}

/* 目录分组：[{label, items, subs:[{label, items}]}] */
export function groupModules(modCatalog = [], zh = true) {
  const gs = [];
  modCatalog.forEach((m) => {
    const gl = zh ? (m.grp?.zh || "") : (m.grp?.en || "");
    let g = gs.find((x) => x.label === gl);
    if (!g) { g = { label: gl, items: [], subs: [] }; gs.push(g); }
    if (m.sub) {
      const sl = zh ? m.sub.zh : m.sub.en;
      let s = g.subs.find((x) => x.label === sl);
      if (!s) { s = { label: sl, items: [] }; g.subs.push(s); }
      s.items.push(m);
    } else g.items.push(m);
  });
  return gs;
}
